/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * A REVIEW IN THE THREAD — its body's grammar and the FOLD (W82 step 4). PURE; `reviewBody.test.ts` and the ONE fold
 * fixture (`__fixtures__/reviewFold.json`, which the phone's `CoachReviewFold.swift` is held to by gate
 * `w82-4-the-review-write`) run it.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * A review and its comments are `coach_messages` rows (`kind = 'review' | 'comment'`, migration `20260926100000`). Their
 * BODY carries what they are about in its first paragraph, then a blank line, then the coach's words:
 *   review   `Reviewed Thursday's Push` [`\n\n` + the overall text]
 *   comment  `Bench Press` `\n\n` + the comment
 * so (1) a phone build that knows nothing of `kind` still shows a sentence that makes sense, and (2) the fold draws from
 * the ROW ALONE — no read of the workout — which is what keeps it whole after the athlete RESETS the workout and the
 * row's `session_id` goes NULL (`on delete set null`; Fable, 2026-09-26).
 *
 * THE FOLD: a review and its comments are ONE item in the thread (`Reviewed Thursday's Push · 3 comments ›`), never ten
 * bubbles. A comment belongs to the review naming the same `session_id`; when the ids are gone (a Reset), to the review
 * written in the SAME statement — the one `Send review` inserts every row at once, so they share `created_at` (Postgres's
 * `now()` is the transaction's instant). A comment with no review is drawn as an ordinary message.
 *
 * REPLIES PER EXERCISE, WITHOUT END (his §8, 2026-09-26): a reply — from either party — is a plain `message` whose
 * `reply_to` chain ROOTS at a comment (the column and its same-thread trigger exist since `20260925160000`). The exercise's
 * thread is every row rooted at its comment; it folds into the review's item too (`· 2 replies`), and the item then stands
 * at its LATEST row's time, so a reply two days later brings the review back to the foot of the conversation rather than
 * hiding under an old bubble. His one-comment bound is untouched: replies are a thread under the ONE comment.
 */

export const REVIEW_BODY_MAX = 4000;
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

/** `Thursday's Push` — the day the workout was FOR (its `scheduled_date`), then its name; `Thursday's workout` unnamed. */
export function reviewTitle(dayKey: string | null, workout: string | null): string {
  const name = workout !== null && workout.trim() !== "" ? workout.trim() : "workout";
  if (dayKey === null || !/^\d{4}-\d{2}-\d{2}/.test(dayKey)) return name === "workout" ? "your workout" : name;
  const d = new Date(`${dayKey.slice(0, 10)}T00:00:00Z`);
  return `${WEEKDAYS[d.getUTCDay()] ?? ""}'s ${name}`;
}

/** The review's first line: `Reviewed Thursday's Push`. ⚠️ It is also what the push says: `send-message-push` (UNCHANGED,
 *  W82 step 4's bound) alerts with the row's own body when the athlete allows previews, so the alert opens with this line. */
export function reviewedLine(title: string): string {
  return `Reviewed ${title}`;
}

function titled(head: string, text: string): string {
  const t = text.trim();
  if (t === "") return head;
  const room = REVIEW_BODY_MAX - head.length - 2;
  return `${head}\n\n${t.length > room ? t.slice(0, room) : t}`;
}

/** A review's body: the line, then the overall text when the coach wrote one (a review with no text is still a review —
 *  it is the mark that the workout had the coach's eye). */
export function reviewBody(title: string, text: string): string {
  return titled(reviewedLine(title), text);
}

/** A comment's body: the exercise's name, then the comment. */
export function commentBody(exercise: string, text: string): string {
  return titled(exercise.trim() === "" ? "Exercise" : exercise.trim(), text);
}

/** Split a titled body back: `{ head, text }` (text "" when there is none). A body with no blank line is all head. */
export function splitTitled(body: string): { head: string; text: string } {
  const i = body.indexOf("\n\n");
  return i < 0 ? { head: body.trim(), text: "" } : { head: body.slice(0, i).trim(), text: body.slice(i + 2).trim() };
}

/* ── the fold ───────────────────────────────────────────────────────────────────────────────────── */

export type FoldRow = {
  readonly id: string;
  readonly kind?: string | null;
  readonly sessionId?: string | null;
  readonly replyTo?: string | null;
  readonly createdAtISO: string;
  readonly body: string;
};

export type Folded<T extends FoldRow> =
  | { readonly kind: "message"; readonly message: T }
  | { readonly kind: "review"; readonly review: T; readonly comments: readonly T[]; readonly replies: readonly T[]; readonly latestISO: string };

/** The row a `reply_to` chain ends at (itself when it quotes nothing, or quotes a row not in `byId`). A cycle — which the
 *  database cannot produce, a reply being written after what it quotes — ends at the row where it closes. */
export function rootOf<T extends FoldRow>(row: T, byId: ReadonlyMap<string, T>): T {
  let at = row;
  const seen = new Set<string>([row.id]);
  for (;;) {
    const up = at.replyTo == null ? undefined : byId.get(at.replyTo);
    if (up === undefined || seen.has(up.id)) return at;
    seen.add(up.id);
    at = up;
  }
}

/** An exercise's thread: every row rooted at its comment (the comment itself excluded), oldest first. */
export function threadOf<T extends FoldRow>(commentId: string, rows: readonly T[]): T[] {
  const byId = new Map(rows.map((r) => [r.id, r]));
  return rows.filter((r) => r.id !== commentId && rootOf(r, byId).id === commentId)
    .sort((a, b) => (a.createdAtISO < b.createdAtISO ? -1 : a.createdAtISO > b.createdAtISO ? 1 : 0));
}

/** Fold a thread (any order in; the input's order kept for what stays, a review standing where its latest row falls is the
 *  CALLER's sort, by `latestISO`): each review carries its comments and every reply rooted at them; they leave the list;
 *  everything else passes through. */
export function foldReviews<T extends FoldRow>(rows: readonly T[]): Folded<T>[] {
  const reviews = rows.filter((r) => r.kind === "review");
  const byId = new Map(rows.map((r) => [r.id, r]));
  const owner = new Map<string, string>();
  for (const c of rows) {
    if (c.kind !== "comment") continue;
    const bySession = c.sessionId != null ? reviews.find((r) => r.sessionId === c.sessionId) : undefined;
    const r = bySession ?? reviews.find((x) => x.createdAtISO === c.createdAtISO);
    if (r !== undefined) owner.set(c.id, r.id);
  }
  const replyOwner = new Map<string, string>();
  for (const m of rows) {
    if (m.kind === "review" || m.kind === "comment" || m.replyTo == null) continue;
    const root = rootOf(m, byId);
    const r = root.kind === "comment" ? owner.get(root.id) : undefined;
    if (r !== undefined) replyOwner.set(m.id, r);
  }
  const out: Folded<T>[] = [];
  for (const r of rows) {
    if ((r.kind === "comment" && owner.has(r.id)) || replyOwner.has(r.id)) continue;
    if (r.kind === "review") {
      const replies = rows.filter((m) => replyOwner.get(m.id) === r.id);
      const latestISO = [r, ...replies].reduce((a, m) => (m.createdAtISO > a ? m.createdAtISO : a), r.createdAtISO);
      out.push({ kind: "review", review: r, comments: rows.filter((c) => c.kind === "comment" && owner.get(c.id) === r.id), replies, latestISO });
    } else out.push({ kind: "message", message: r });
  }
  return out;
}

const count = (n: number, one: string) => `${String(n)} ${n === 1 ? one : `${one}s`}`;

/** The fold's line: `Reviewed Thursday's Push · 3 comments · 2 replies` (the `›` is the link's, drawn by the page). */
export function foldLine(review: Pick<FoldRow, "body">, comments: number, replies = 0): string {
  const parts = [splitTitled(review.body).head];
  if (comments > 0) parts.push(count(comments, "comment"));
  if (replies > 0) parts.push(count(replies, "reply").replace("replys", "replies"));
  return parts.join(" · ");
}

/**
 * THE EXERCISE THREADS AWAITING THE COACH (his priority, §8(6), 2026-09-26: *"first review, then replying to these"*): a
 * comment's thread whose LAST row — the comment itself when nobody has answered it — was not written by the coach. The
 * Inbox's own rule (`threadRows`: "the last message said decides who owes a reply"), applied per thread. Oldest wait first.
 */
export function repliesWaiting<T extends FoldRow & { readonly fromCoach: boolean }>(rows: readonly T[]): { comment: T; sinceISO: string }[] {
  const out: { comment: T; sinceISO: string }[] = [];
  for (const c of rows) {
    if (c.kind !== "comment") continue;
    const last = threadOf(c.id, rows).at(-1) ?? c;
    if (!last.fromCoach) out.push({ comment: c, sinceISO: last.createdAtISO });
  }
  return out.sort((a, b) => (a.sinceISO < b.sinceISO ? -1 : a.sinceISO > b.sinceISO ? 1 : a.comment.id < b.comment.id ? -1 : 1));
}

/** Next week's exercise row, his bound: the ONE comment, and `2 replies ›` when its thread grew; null for none. */
export function repliesLink(replies: number): string | null {
  return replies === 0 ? null : `${count(replies, "reply").replace("replys", "replies")} ›`;
}
